/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './patient-name.reducer';

export const PatientName = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const patientNameList = useAppSelector(state => state.patientName.entities);
  const loading = useAppSelector(state => state.patientName.loading);
  const totalItems = useAppSelector(state => state.patientName.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="patient-name-heading" data-cy="PatientNameHeading">
        <Translate contentKey="tinyMedicRestApp.patientName.home.title">Patient Names</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.patientName.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/patient-name/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.patientName.home.createLabel">Create new Patient Name</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.patientName.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {patientNameList && patientNameList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('preferred')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.preferred">Preferred</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('preferred')} />
                </th>
                <th className="hand" onClick={sort('givenName')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.givenName">Given Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('givenName')} />
                </th>
                <th className="hand" onClick={sort('prefix')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.prefix">Prefix</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('prefix')} />
                </th>
                <th className="hand" onClick={sort('middleName')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.middleName">Middle Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('middleName')} />
                </th>
                <th className="hand" onClick={sort('familyNamePrefix')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.familyNamePrefix">Family Name Prefix</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('familyNamePrefix')} />
                </th>
                <th className="hand" onClick={sort('familyName')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.familyName">Family Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('familyName')} />
                </th>
                <th className="hand" onClick={sort('familyNameSufix')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.familyNameSufix">Family Name Sufix</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('familyNameSufix')} />
                </th>
                <th className="hand" onClick={sort('familyName2')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.familyName2">Family Name 2</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('familyName2')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('created')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.created">Created</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('created')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('totalReading')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.totalReading">Total Reading</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalReading')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('uuid')}>
                  <Translate contentKey="tinyMedicRestApp.patientName.uuid">Uuid</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('uuid')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientName.doctor">Doctor</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientName.pharmacist">Pharmacist</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientName.diaglabAssistant">Diaglab Assistant</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientName.hospitalAssistant">Hospital Assistant</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.patientName.patient">Patient</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {patientNameList.map((patientName, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/patient-name/${patientName.id}`} color="link" size="sm">
                      {patientName.id}
                    </Button>
                  </td>
                  <td>{patientName.preferred ? 'true' : 'false'}</td>
                  <td>{patientName.givenName}</td>
                  <td>{patientName.prefix}</td>
                  <td>{patientName.middleName}</td>
                  <td>{patientName.familyNamePrefix}</td>
                  <td>{patientName.familyName}</td>
                  <td>{patientName.familyNameSufix}</td>
                  <td>{patientName.familyName2}</td>
                  <td>{patientName.active ? 'true' : 'false'}</td>
                  <td>{patientName.created ? <TextFormat type="date" value={patientName.created} format={APP_DATE_FORMAT} /> : null}</td>
                  <td>{patientName.microSec}</td>
                  <td>{patientName.timestampentry}</td>
                  <td>{patientName.totalReading}</td>
                  <td>{patientName.lastupdated}</td>
                  <td>{patientName.cursor}</td>
                  <td>{patientName.uuid}</td>
                  <td>{patientName.doctor ? <Link to={`/doctor/${patientName.doctor.id}`}>{patientName.doctor.id}</Link> : ''}</td>
                  <td>
                    {patientName.pharmacist ? <Link to={`/pharmacist/${patientName.pharmacist.id}`}>{patientName.pharmacist.id}</Link> : ''}
                  </td>
                  <td>
                    {patientName.diaglabAssistant ? (
                      <Link to={`/diaglab-assistant/${patientName.diaglabAssistant.id}`}>{patientName.diaglabAssistant.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {patientName.hospitalAssistant ? (
                      <Link to={`/hospital-assistant/${patientName.hospitalAssistant.id}`}>{patientName.hospitalAssistant.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{patientName.patient ? <Link to={`/gluco-user/${patientName.patient.id}`}>{patientName.patient.id}</Link> : ''}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/patient-name/${patientName.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/patient-name/${patientName.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/patient-name/${patientName.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.patientName.home.notFound">No Patient Names found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={patientNameList && patientNameList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default PatientName;
